import { apiFetch } from '@/lib/api';

/**
 * Search criteria for venue research
 * Supports both legacy single-city and new multi-city/country formats
 */
export interface CreateResearchInput {
  // Multi-location format (new) - supports both cities and countries
  search_cities?: Array<{
    city: string; // Full formatted address from Google Places (e.g., "Toronto, ON, Canada" or "Spain")
    place_id?: string;
    city_only?: string; // Locality from Google Places (e.g., "Toronto") - used for DB matching
    location_type?: 'city' | 'country' | 'region'; // Type of location
  }>;
  search_sequences?: number; // 1-5

  // Legacy single-city format (backward compatibility)
  search_city?: string;
  search_city_place_id?: string;
  search_city_only?: string;
  location_type?: 'city' | 'country' | 'region';
  
  // Filter criteria (applies to all locations)
  min_rooms?: number;
  min_conference_capacity?: number;
  min_stars?: number;
  additional_instructions?: string;
}

/**
 * Research job record
 */
export interface VenueResearch {
  id: string;
  organization_id: string;
  user_id: string | null;
  search_city: string;
  search_city_place_id: string | null;
  min_rooms: number | null;
  min_conference_capacity: number | null;
  min_stars: number | null;
  additional_instructions: string | null;
  // Multi-city metadata
  search_sequence: number;
  parent_research_id: string | null;
  city_index: number;
  total_cities: number;
  total_sequences: number;
  job_id: string | null;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  started_at: string | null;
  completed_at: string | null;
  duration_seconds: number | null;
  llm_log_id: string | null;
  llm_model: string | null;
  llm_total_cost_usd: string | null;
  venues_found: number;
  venues_created: number;
  venues_updated: number;
  search_criteria_json: SearchCriteriaJson | null;
  llm_response_json: LLMResponseJson | null;
  agent_notes: string[] | null;
  discovered_venues_json: string[] | null;
  error_message: string | null;
  error_code: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Search criteria stored in JSON
 */
export interface SearchCriteriaJson {
  search_city: string;
  search_city_place_id?: string;
  min_rooms?: number;
  min_conference_capacity?: number;
  min_stars?: number;
  additional_instructions?: string;
  // Multi-city context
  search_sequence?: number;
  total_sequences?: number;
  city_index?: number;
  total_cities?: number;
}

/**
 * Discovered hotel from LLM
 */
export interface DiscoveredHotel {
  property_name: string;
  address: string | null;
  city: string;
  country: string | null;
  state: string | null;
  region: string | null;
  website: string | null;
  number_of_rooms: number;
  meeting_spaces: number;
  maximum_capacity: number;
  star_rating: number | null;
}

/**
 * LLM response with hotels and agent notes
 */
export interface LLMResponseJson {
  hotels: DiscoveredHotel[];
  agent_notes: string[];
}

/**
 * Response from create research endpoint
 */
export interface CreateResearchResponse {
  researchIds: string[];
  jobIds: string[];
  totalJobs: number;
  message: string;
  // Backward compatibility: included when totalJobs === 1
  researchId?: string;
  jobId?: string;
}

/**
 * List research response with pagination
 */
export interface ListResearchResponse {
  items: VenueResearch[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
}

/**
 * Venue research service for API calls
 */
export class VenueResearchService {
  /**
   * Create a new research job
   */
  static async createResearch(input: CreateResearchInput): Promise<CreateResearchResponse> {
    const response = await apiFetch('/api/venues/research', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to create research job');
    }

    const result = await response.json();
    return result.data;
  }

  /**
   * List research jobs with pagination and filters
   */
  static async listResearch(
    page = 1,
    pageSize = 20,
    status?: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled'
  ): Promise<ListResearchResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });

    if (status) {
      params.append('status', status);
    }

    const response = await apiFetch(`/api/venues/research?${params.toString()}`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to fetch research list');
    }

    const result = await response.json();
    return result.data;
  }

  /**
   * Get research details by ID
   */
  static async getResearchById(id: string): Promise<VenueResearch> {
    const response = await apiFetch(`/api/venues/research/${id}`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to fetch research details');
    }

    const result = await response.json();
    return result.data;
  }

  /**
   * Get venues discovered by research job
   */
  static async getResearchVenues(id: string): Promise<any[]> {
    const response = await apiFetch(`/api/venues/research/${id}/venues`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'Failed to fetch research venues');
    }

    const result = await response.json();
    return result.data.venues;
  }
}
